import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { CONTEXTS } from 'figshelf-modules';
import userStatusSocketService, { OnlineStatus } from '../services/sub/UserStatusSocketService';

const UserStatusContext = createContext();

export const useUserStatusContext = () => {
  const context = useContext(UserStatusContext);
  if (!context) {
    throw new Error('useUserStatusContext must be used within UserStatusProvider');
  }
  return context;
};

export const UserStatusProvider = ({ children }) => {
  const { user, isSignedIn } = CONTEXTS.useAuth();
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [userStatuses, setUserStatuses] = useState(new Map());
  const [isInitialized, setIsInitialized] = useState(false);
  const [updateCounter, setUpdateCounter] = useState(0);

  // Initialize socket service
  useEffect(() => {
    if (!isSignedIn || !user) {
      // Disconnect if user signs out
      if (isInitialized) {
        userStatusSocketService.disconnect().catch(console.error);
        setIsInitialized(false);
        setOnlineUsers(new Set());
        setUserStatuses(new Map());
      }
      return;
    }

    const initializeService = async () => {
      try {
        await userStatusSocketService.initialize();
        setIsInitialized(true);

        // Get initial online users
        const initialOnlineUsers = userStatusSocketService.getOnlineUsers();
        setOnlineUsers(new Set(initialOnlineUsers));
      } catch (error) {
        console.error('❌ UserStatusContext: Failed to initialize:', error);
      }
    };

    initializeService();

    // Subscribe to status changes
    const unsubscribe = userStatusSocketService.onStatusChange((data) => {
      if (data.type === 'onlineUsersUpdated') {
        // Update online users list
        const userIds = data.onlineUsers.map(u => u.userId);
        setOnlineUsers(new Set(userIds));
        
        // Update statuses map
        setUserStatuses(prev => {
          const newMap = new Map(prev);
          data.onlineUsers.forEach(u => {
            newMap.set(u.userId, u.status);
          });
          return newMap;
        });
        
        setUpdateCounter(prev => prev + 1);
      } else if (data.type === 'statusChanged' && data.userId) {
        // Update specific user's status
        setUserStatuses(prev => {
          const newMap = new Map(prev);
          newMap.set(data.userId, data.status);
          return newMap;
        });

        // Update online users set
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          if (data.isOnline) {
            newSet.add(data.userId);
          } else {
            newSet.delete(data.userId);
          }
          return newSet;
        });
        
        setUpdateCounter(prev => prev + 1);
      }
    });

    // Cleanup
    return () => {
      unsubscribe();
      userStatusSocketService.stopHeartbeat();
    };
  }, [isSignedIn, user]);

  // Check if user is online
  const isUserOnline = useCallback((userId) => {
    return onlineUsers.has(userId);
  }, [onlineUsers]);

  // Get user status
  const getUserStatus = useCallback((userId) => {
    return userStatuses.get(userId) || OnlineStatus.OFFLINE;
  }, [userStatuses]);

  // Change current user's status
  const changeStatus = useCallback((newStatus) => {
    if (isInitialized) {
      userStatusSocketService.changeStatus(newStatus);
    }
  }, [isInitialized]);

  const value = {
    onlineUsers,
    userStatuses,
    isInitialized,
    updateCounter,
    isUserOnline,
    getUserStatus,
    changeStatus,
  };

  return (
    <UserStatusContext.Provider value={value}>
      {children}
    </UserStatusContext.Provider>
  );
};

export default UserStatusContext;
